import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { recordingModeActive } from "@/lib/recordingMode";
import type { User } from "@shared/schema";
import { getQueryFn } from "@/lib/queryClient";

// /api/auth/user augments the User row with a server-computed isDemoAdmin
// flag (server/routes.ts). Mirror that shape here so callers don't have to
// cast every time they want to check admin-only UI.
type AuthUser = User & {
  isDemoAdmin?: boolean;
  linkedProviders?: string[];
  canBarter?: boolean;
};

export function useAuth() {
  const { data: user, isLoading } = useQuery<AuthUser | null>({
    queryKey: ["/api/auth/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
  });

  // App holds the whole tree behind this flag, so it must only ever be true
  // before the first answer. Offline, this request fails and is retried, and
  // each retry put the query back to pending — which tore the rendered app down
  // and replaced it with the "Loading…" screen, with no way back until the
  // connection returned. After the first settle (success, 401, or network
  // failure) we know enough to render; later attempts refresh in the background.
  const settled = useRef(false);
  if (!isLoading) settled.current = true;

  return {
    user: user ?? null,
    isLoading: isLoading && !settled.current,
    isAuthenticated: !!user,
    isAdmin: !!user?.isDemoAdmin && !recordingModeActive(),
  };
}
